'use client';

import { useEffect, useRef } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi, getStoredToken, TokenManager } from '@tankef/api-client';
import {
  Party,
  type AccountRole,
  type NotificationData,
  type NotificationPreferences,
  mapAccountInvitation,
  mapAccountUser,
  notificationFromApi,
  notificationPreferencesFromApi,
} from '@tankef/core';

import { useClientAuthStore, useNoticesStore } from '../stores';
export {
  useRealtimeNotifications,
  type Notification,
  type ConnectionStatus,
} from './client/useRealtimeNotifications';

// ============================================================================
// AUTH HOOKS
// ============================================================================

/**
 * Direct Auth login (HS256 legacy).
 * Used when oauth_pkce_enabled feature flag is disabled.
 */
export function useClientLogin() {
  const setAuth = useClientAuthStore(s => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const response = await clientApi.login(email, password);
      return response;
    },
    onSuccess: data => {
      // El API devuelve 'user' en el login, convertirlo a Party
      const party = Party.fromApi(data.user!);
      setAuth(party, data.token, data.refresh_token);
      queryClient.clear();
    },
  });
}

/**
 * MFA Login - Completa el login cuando el usuario tiene 2FA habilitado.
 * Se usa después de que el login normal retorna error 'mfa_required'.
 */
export function useLoginWithMFA() {
  const setAuth = useClientAuthStore(s => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mfaToken, mfaCode }: { mfaToken: string; mfaCode: string }) => {
      const authResponse = await clientApi.loginWithMFA(mfaToken, mfaCode);
      // No llamamos a ningún endpoint adicional - el dashboard maneja party: null
      return authResponse;
    },
    onSuccess: data => {
      // Party será null - el dashboard lo maneja con optional chaining (party?.firstName)
      setAuth(null, data.token, data.refresh_token);
      queryClient.clear();
    },
  });
}

/**
 * OAuth 2.0 + PKCE login.
 * Redirects the user to the authorization server. The callback page handles the token exchange.
 * Used when oauth_pkce_enabled feature flag is enabled.
 */
export function useOAuthLogin() {
  return useMutation({
    mutationFn: async () => {
      const { buildAuthorizeUrl } = await import('@tankef/api-client/oauth');
      const { url } = await buildAuthorizeUrl();
      window.location.href = url;
    },
  });
}

export function useClientLogout() {
  const clearAuth = useClientAuthStore(s => s.clearAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Revoke OAuth token if it's an ES256 token
      const token = TokenManager.getAccessToken();
      if (token) {
        try {
          const header = JSON.parse(atob(token.split('.')[0]!));
          if (header.alg === 'ES256') {
            const { revokeOAuthToken } = await import('@tankef/api-client/oauth');
            await revokeOAuthToken(token);
          }
        } catch {
          // Ignore parse errors — proceed with logout
        }
      }
      await clientApi.logout();
    },
    onSettled: () => {
      clearAuth();
      queryClient.clear();
    },
  });
}

export function useClientIsAuthenticated(): boolean {
  return useClientAuthStore(s => s.isAuthenticated);
}

export function useClientIsLoading(): boolean {
  return useClientAuthStore(s => s.isLoading);
}

export function useParty(): Party | null {
  return useClientAuthStore(s => s.party);
}

/**
 * Refresh the authenticated party from backend.
 * The zustand store is persisted, so KYC and profile fields can become stale
 * after onboarding or external updates unless we rehydrate from /auth/me.
 */
export function useCurrentPartyProfile(enabled = true) {
  const isAuthenticated = useClientAuthStore(s => s.isAuthenticated);
  const currentParty = useClientAuthStore(s => s.party);
  const setParty = useClientAuthStore(s => s.setParty);

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => clientApi.me(),
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    retry: 1,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!query.data) return;

    const nextParty = query.data;
    const hasChanged =
      !currentParty ||
      currentParty.id !== nextParty.id ||
      currentParty.firstName !== nextParty.firstName ||
      currentParty.lastName !== nextParty.lastName ||
      currentParty.email !== nextParty.email ||
      currentParty.phone !== nextParty.phone ||
      currentParty.kycLevel !== nextParty.kycLevel ||
      currentParty.status !== nextParty.status;

    if (hasChanged) {
      setParty(nextParty);
    }
  }, [currentParty, query.data, setParty]);

  return query;
}

export function useClientProfile(enabled = true) {
  const isAuthenticated = useClientAuthStore(s => s.isAuthenticated);

  return useQuery({
    queryKey: ['client', 'profile'],
    queryFn: () => clientApi.clientProfile(),
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useMockLogin() {
  const setAuth = useClientAuthStore(s => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_: { email: string; password: string }) => {
      await new Promise(resolve => setTimeout(resolve, 400));
      const iat = Math.floor(Date.now() / 1000);
      const token = `mock-jwt-${btoa(JSON.stringify({ sub: 'user-1', email: 'demo@tankef.com', iat, exp: iat + 3600 }))}`;
      const refreshToken = `mock-refresh-user-1-${Date.now()}`;
      return { token, refreshToken };
    },
    onSuccess: ({ token, refreshToken }) => {
      const party = Party.fromApi({
        id: 'user-1',
        email: 'demo@tankef.com',
        phone: '5512345678',
        first_name: 'Usuario',
        last_name: 'Demo',
        kyc_level: 2,
        status: 'active',
        created_at: '2024-01-01T00:00:00Z',
      });
      TokenManager.updateTokens(token, refreshToken, 3600, '');
      setAuth(party, token, refreshToken);
      queryClient.clear();
    },
  });
}

// ============================================================================
// ACCOUNT HOOKS
// ============================================================================

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => clientApi.accounts(),
    staleTime: 30000,
  });
}

export function usePrimaryAccount() {
  return useQuery({
    queryKey: ['accounts', 'primary'],
    queryFn: () => clientApi.primaryAccount(),
    staleTime: 30000,
  });
}

// ============================================================================
// ACCOUNT MULTI-USER HOOKS (PM, F7 plan persona-moral)
// Gestión de usuarios/roles del dashboard PM. Usan apiClient + X-Account-Id
// (lo inyecta el adapter por-llamada). Los mappers normalizan snake→camel.
// ============================================================================

/** Lista los usuarios de una cuenta PM (mapeados). */
export function useAccountUsers(accountId: string | undefined) {
  return useQuery({
    queryKey: ['accounts', accountId, 'users'],
    queryFn: async () => {
      const data = await clientApi.getAccountUsers(accountId as string);
      return (data.users ?? []).map(mapAccountUser);
    },
    enabled: Boolean(accountId),
    staleTime: 30000,
  });
}

/** Invita un usuario a la cuenta. Invalida la lista de usuarios al éxito. */
export function useInviteAccountUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string; email: string; role: AccountRole }) =>
      clientApi
        .createAccountInvitation(vars.accountId, { email: vars.email, role: vars.role })
        .then(mapAccountInvitation),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['accounts', vars.accountId, 'users'] });
    },
  });
}

/** Cambia el rol de un usuario de la cuenta. */
export function useUpdateAccountUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string; userId: string; role: AccountRole }) =>
      clientApi.updateAccountUserRole(vars.accountId, vars.userId, vars.role).then(mapAccountUser),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['accounts', vars.accountId, 'users'] });
    },
  });
}

/** Remueve (soft-delete) un usuario de la cuenta. */
export function useRemoveAccountUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { accountId: string; userId: string; reason?: string }) =>
      clientApi.removeAccountUser(vars.accountId, vars.userId, vars.reason),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['accounts', vars.accountId, 'users'] });
    },
  });
}

/** Acepta una invitación (lado invitee, token single-use). */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (token: string) => clientApi.acceptInvitation(token),
  });
}

// ============================================================================
// TRANSFER HOOKS
// ============================================================================

export function useTransfers(params?: { page?: number; status?: string }) {
  return useQuery({
    queryKey: ['transfers', params],
    queryFn: () => clientApi.transfers(params),
    staleTime: 30000,
  });
}

export function useTransferSummary() {
  return useQuery({
    queryKey: ['transfers', 'summary'],
    queryFn: () => clientApi.transferSummary(),
    staleTime: 60_000,
  });
}

export function useTransfer(id: string) {
  return useQuery({
    queryKey: ['transfers', id],
    queryFn: () => clientApi.transfer(id),
    enabled: !!id,
  });
}

export function useCreateTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      data: Parameters<typeof clientApi.createTransfer>[0];
      idempotencyKey?: string;
      stepUpToken?: string;
    }) => clientApi.createTransfer(params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useCancelTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => clientApi.cancelTransfer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// ============================================================================
// BENEFICIARY HOOKS
// ============================================================================

export function useBeneficiaries() {
  return useQuery({
    queryKey: ['beneficiaries'],
    queryFn: () => clientApi.beneficiaries(),
    staleTime: 60000,
  });
}

export function useCreateBeneficiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof clientApi.createBeneficiary>[0]) =>
      clientApi.createBeneficiary(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
    },
  });
}

export function useDeleteBeneficiary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => clientApi.deleteBeneficiary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beneficiaries'] });
    },
  });
}

// ============================================================================
// NOTIFICATION HOOKS
// ============================================================================

/** Parameters for fetching notifications */
export interface UseNotificationsParams {
  /** Filter by read status (true = read, false = unread) */
  read?: boolean;
  /** Filter by category */
  category?: string;
  /** Page number (1-indexed) */
  page?: number;
  /** Number of items per page */
  perPage?: number;
}

/** Response from useNotifications hook */
export interface NotificationsQueryResult {
  data: NotificationData[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    unreadCount: number;
  };
}

/**
 * Hook to fetch paginated notifications
 * @param params - Query parameters for filtering/pagination
 */
export function useNotifications(params?: UseNotificationsParams) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: async (): Promise<NotificationsQueryResult> => {
      const response = await clientApi.notifications({
        read: params?.read,
        category: params?.category,
        page: params?.page,
        per_page: params?.perPage,
      });

      return {
        data: response.data.map(notificationFromApi),
        meta: {
          currentPage: response.meta.current_page,
          totalPages: response.meta.total_pages,
          totalCount: response.meta.total_count,
          perPage: response.meta.per_page,
          unreadCount: response.meta.unread_count,
        },
      };
    },
    staleTime: 30000,
    refetchInterval: 60000, // Refetch every minute
  });
}

/**
 * Hook to fetch a single notification by ID
 * @param id - Notification UUID
 */
export function useNotification(id: string) {
  return useQuery({
    queryKey: ['notifications', id],
    queryFn: async (): Promise<NotificationData> => {
      const response = await clientApi.getNotification(id);
      return notificationFromApi(response);
    },
    enabled: !!id,
  });
}

/**
 * Hook to mark a single notification as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await clientApi.markNotificationAsRead(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Result from marking all notifications as read */
export interface MarkAllReadResult {
  markedAsRead: number;
  message: string;
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<MarkAllReadResult> => {
      const result = await clientApi.markAllNotificationsAsRead();
      return {
        markedAsRead: result.marked_as_read,
        message: result.message,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to fetch notification preferences
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: async (): Promise<NotificationPreferences> => {
      const response = await clientApi.getNotificationPreferences();
      return notificationPreferencesFromApi(response);
    },
  });
}

/**
 * Hook to update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<NotificationPreferences>) => {
      // Convert to API format
      const apiData: Parameters<typeof clientApi.updateNotificationPreferences>[0] = {};

      if (data.channels) {
        apiData.channels = {};
        for (const [channelName, channelData] of Object.entries(data.channels)) {
          apiData.channels[channelName] = {
            enabled: channelData.enabled,
          };
          if (Object.keys(channelData.categories).length > 0) {
            apiData.channels[channelName].categories = channelData.categories;
          }
        }
      }

      return clientApi.updateNotificationPreferences(apiData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });
}

// ============================================================================
// WALLET HOOKS
// ============================================================================

export type MovementType = 'income' | 'expense';
export type MovementStatus = 'pending' | 'completed' | 'failed' | 'cancelled';
export type MovementKind = 'cargo' | 'abono';
export type MovementDirection = 'debit' | 'credit';

export interface WalletBalance {
  availableCents: number;
  heldCents: number;
  totalCents: number;
  currency: string;
  accountId: string;
  clabe: string;
}

export interface Movement {
  id: string;
  type: MovementType;
  status: MovementStatus;
  description: string;
  reference?: string;
  amountCents: number;
  currency: string;
  date: Date;
  counterparty?: {
    name: string;
    clabe?: string;
  };
  // Backend wallet/movements extras
  journalEntryId?: string;
  entryNumber?: string;
  entryDate?: string;
  movementType?: MovementKind;
  direction?: MovementDirection;
  amountFormatted?: string;
  folio?: string;
  rawStatus?: string;
  statusLabel?: string;
  entryType?: string;
}

export interface MovementsFilter {
  startDate?: string;
  endDate?: string;
  type?: MovementType;
  status?: MovementStatus;
  direction?: MovementDirection;
}

export function useWalletBalance() {
  const { data: primaryAccount } = usePrimaryAccount();

  return useQuery({
    queryKey: ['wallet', 'balance', primaryAccount?.id],
    queryFn: async (): Promise<WalletBalance> => {
      if (!primaryAccount?.id) {
        throw new Error('Primary account not found');
      }
      const data = await clientApi.accountBalance(primaryAccount.id);
      return {
        availableCents: data.available_cents ?? data.available_balance_cents ?? 0,
        heldCents: data.held_cents ?? data.hold_balance_cents ?? data.held_balance_cents ?? 0,
        totalCents: data.total_cents ?? data.ledger_balance_cents ?? 0,
        currency: data.currency ?? data.currency_code ?? 'MXN',
        accountId: data.account_id ?? primaryAccount.id,
        clabe: data.clabe,
      };
    },
    enabled: !!primaryAccount?.id,
    staleTime: 30000, // 30 seconds
  });
}

export function useWalletSummary() {
  return useQuery({
    queryKey: ['wallet', 'summary'],
    queryFn: () => clientApi.walletSummary(),
    staleTime: 60_000,
  });
}

const STATUS_MAP: Record<string, MovementStatus> = {
  liquidado: 'completed',
  completado: 'completed',
  completed: 'completed',
  pendiente: 'pending',
  pending: 'pending',
  fallido: 'failed',
  failed: 'failed',
  cancelado: 'cancelled',
  cancelled: 'cancelled',
};

function parseCurrencyFromAmount(amount: string): string {
  const parts = amount?.trim().split(/\s+/);
  return parts && parts.length > 1 ? (parts[parts.length - 1] ?? 'MXN') : 'MXN';
}

export function useAccountTransactions(
  accountId: string | undefined,
  filters?: MovementsFilter,
  page = 1,
  pageSize = 10
) {
  return useQuery({
    queryKey: ['accounts', accountId, 'transactions', filters, page, pageSize],
    queryFn: async () => {
      if (!accountId) {
        throw new Error('accountId is required');
      }
      const response = await clientApi.accountTransactions(accountId, {
        page,
        per_page: pageSize,
        date_from: filters?.startDate,
        date_to: filters?.endDate,
        direction: filters?.direction,
      });

      const movements: Movement[] = response.data.map(m => {
        // Always derive from movement_type to keep parity with useMovements.
        // The backend's `type` field on /accounts/{id}/transactions is inverted
        // relative to the convention used elsewhere in the app.
        const type: MovementType = m.movement_type === 'abono' ? 'income' : 'expense';
        const status = STATUS_MAP[m.status] ?? 'pending';
        return {
          id: m.id,
          type,
          status,
          description: m.description,
          reference: m.reference ?? undefined,
          amountCents: m.amount_cents,
          currency: parseCurrencyFromAmount(m.amount),
          date: new Date(m.created_at),
          journalEntryId: m.journal_entry_id,
          entryNumber: m.entry_number,
          entryDate: m.entry_date,
          movementType: m.movement_type,
          direction: m.direction,
          amountFormatted: m.amount,
          folio: m.folio,
          rawStatus: m.status,
          statusLabel: m.status_label,
          entryType: m.entry_type,
        };
      });

      return {
        movements,
        meta: {
          page: response.meta.current_page,
          pageSize: response.meta.per_page,
          total: response.meta.total_count,
          totalPages: response.meta.total_pages,
        },
      };
    },
    enabled: !!accountId,
    staleTime: 30000,
  });
}

export function useMovements(filters?: MovementsFilter, page = 1, pageSize = 10) {
  return useQuery({
    queryKey: ['wallet', 'movements', filters, page, pageSize],
    queryFn: async () => {
      const response = await clientApi.movements({
        page,
        per_page: pageSize,
        start_date: filters?.startDate,
        end_date: filters?.endDate,
      });

      const movements: Movement[] = response.data.movements.map(m => {
        const type: MovementType = m.movement_type === 'abono' ? 'income' : 'expense';
        const status = STATUS_MAP[m.status] ?? 'pending';
        return {
          id: m.id,
          type,
          status,
          description: m.description,
          reference: m.reference ?? undefined,
          amountCents: m.amount_cents,
          currency: parseCurrencyFromAmount(m.amount),
          date: new Date(m.created_at),
          journalEntryId: m.journal_entry_id,
          entryNumber: m.entry_number,
          entryDate: m.entry_date,
          movementType: m.movement_type,
          direction: m.direction,
          amountFormatted: m.amount,
          folio: m.folio,
          rawStatus: m.status,
          statusLabel: m.status_label,
          entryType: m.entry_type,
        };
      });

      const total = response.meta.total;
      const perPage = response.meta.per_page;
      return {
        movements,
        meta: {
          page: response.meta.page,
          pageSize: perPage,
          total,
          totalPages: perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1,
        },
      };
    },
    staleTime: 30000,
  });
}

export function useDownloadWalletReport() {
  return useMutation({
    mutationFn: async (params: {
      type: 'movements' | 'statements';
      startDate: string;
      endDate: string;
      format?: 'pdf' | 'csv' | 'xlsx';
    }) => {
      const response = await clientApi.downloadWalletReport({
        type: params.type,
        start_date: params.startDate,
        end_date: params.endDate,
        format: params.format,
      });
      return response;
    },
  });
}

// ============================================================================
// NOTICES HOOKS
// ============================================================================

export function useClientNotices() {
  const setNotices = useNoticesStore(s => s.setNotices);

  return useQuery({
    queryKey: ['notices'],
    queryFn: async () => {
      const notices = await clientApi.notices();
      setNotices(notices);
      return notices;
    },
    staleTime: 60000,
    refetchInterval: 60000,
  });
}

// ============================================================================
// REGISTRATION HOOKS
// ============================================================================

/**
 * Hook para obtener la ubicación del usuario y hacer reverse geocoding
 */
export function useLocation() {
  return useMutation({
    mutationFn: async (): Promise<{
      latitude: number;
      longitude: number;
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      isInMexico: boolean;
    }> => {
      // Get geolocation
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocalización no soportada'));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocoding with Nominatim (OpenStreetMap)
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
          {
            headers: {
              'Accept-Language': 'es',
            },
          }
        );
        const data = await response.json();

        const address = data.address || {};
        const country = address.country || '';
        const isInMexico =
          country.toLowerCase().includes('méxico') || country.toLowerCase().includes('mexico');

        return {
          latitude,
          longitude,
          address: data.display_name,
          city: address.city || address.town || address.village,
          state: address.state,
          country,
          isInMexico,
        };
      } catch {
        // If reverse geocoding fails, just return coordinates
        return {
          latitude,
          longitude,
          isInMexico: false,
        };
      }
    },
  });
}

/**
 * Hook para enviar código de verificación por email
 */
export function useSendEmailCode() {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await clientApi.sendEmailVerification(email);
      return response;
    },
  });
}

/**
 * Hook para verificar código de email
 */
export function useVerifyEmail() {
  return useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      const response = await clientApi.verifyEmail(email, code);
      return response;
    },
  });
}

/**
 * Hook para enviar código de verificación por SMS
 */
export function useSendPhoneCode() {
  return useMutation({
    mutationFn: async (phone: string) => {
      const response = await clientApi.sendPhoneVerification(phone);
      return response;
    },
  });
}

/**
 * Hook para verificar código de teléfono
 */
export function useVerifyPhone() {
  return useMutation({
    mutationFn: async ({ phone, code }: { phone: string; code: string }) => {
      const response = await clientApi.verifyPhone(phone, code);
      return response;
    },
  });
}

/**
 * Hook para solicitar permiso de cámara
 */
type CameraPermissionFailureReason =
  | 'permission_denied'
  | 'policy_blocked'
  | 'insecure_context'
  | 'unsupported'
  | 'device_not_found'
  | 'device_unavailable'
  | 'unknown';

interface CameraPermissionResult {
  granted: boolean;
  stream?: MediaStream;
  reason?: CameraPermissionFailureReason;
  errorMessage?: string;
}

function classifyCameraPermissionError(
  error: unknown
): Pick<CameraPermissionResult, 'reason' | 'errorMessage'> {
  const errorMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
  const normalizedMessage = errorMessage?.toLowerCase() ?? '';
  const errorName = error instanceof Error ? error.name : '';

  if (
    normalizedMessage.includes('permissions policy') ||
    normalizedMessage.includes('camera is not allowed in this document') ||
    normalizedMessage.includes('not allowed in this document')
  ) {
    return { reason: 'policy_blocked', errorMessage };
  }

  switch (errorName) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return { reason: 'permission_denied', errorMessage };
    case 'SecurityError':
      return {
        reason:
          normalizedMessage.includes('permissions policy') ||
          normalizedMessage.includes('not allowed in this document')
            ? 'policy_blocked'
            : 'permission_denied',
        errorMessage,
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return { reason: 'device_not_found', errorMessage };
    case 'NotReadableError':
    case 'TrackStartError':
      return { reason: 'device_unavailable', errorMessage };
    case 'OverconstrainedError':
    case 'TypeError':
      return { reason: 'unsupported', errorMessage };
    default:
      return { reason: 'unknown', errorMessage };
  }
}

export function useCameraPermission() {
  return useMutation({
    mutationFn: async (): Promise<CameraPermissionResult> => {
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        return { granted: false, reason: 'insecure_context' };
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        return { granted: false, reason: 'unsupported' };
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
        });
        return { granted: true, stream };
      } catch (error) {
        return {
          granted: false,
          ...classifyCameraPermissionError(error),
        };
      }
    },
  });
}

/**
 * Hook para completar el registro
 */
export function useCompleteRegistration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      email: string;
      phone: string;
      password: string;
      location: {
        latitude: number;
        longitude: number;
      };
      personalData?: {
        nombres: string;
        apellidoPaterno: string;
        apellidoMaterno?: string;
      };
    }) => {
      const response = await clientApi.register(data);
      return response;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// ============================================================================
// ONBOARDING HOOKS (see ./client/useOnboarding.ts)
// ============================================================================

export * from './client/useOnboarding';

/**
 * Hook para reenviar código OTP en el proceso de onboarding
 * Usa el onboarding_token para identificar la sesión
 */
export function useOnboardingResendCode() {
  return useMutation({
    mutationFn: async (onboardingToken: string) => {
      return clientApi.onboardingResendCode(onboardingToken);
    },
  });
}

/**
 * Hook para verificar código OTP de teléfono en el proceso de onboarding
 */
export function useOnboardingVerifyPhone() {
  return useMutation({
    mutationFn: async ({ onboardingToken, code }: { onboardingToken: string; code: string }) => {
      return clientApi.onboardingVerifyPhone(onboardingToken, code);
    },
  });
}

// ============================================================================
// ROTATING SAVINGS HOOKS
// ============================================================================

export interface RotatingSavingsResponse {
  id: string;
  folio: string;
  name: string;
  period: 'weekly' | 'biweekly' | 'monthly';
  startDate: Date;
  endDate: Date;
  amountCents: number;
  contributionCents: number;
  bonusCents: number;
  status: 'active' | 'pending' | 'completed' | 'cancelled';
  link?: string;
}

export interface RotatingSavingsSummary {
  totalBalanceCents: number;
  activeCount: number;
  pendingContributionsCents: number;
}

// ============================================================================
// INVESTMENTS HOOKS
// ============================================================================

/**
 * Investment status type
 */
export type InvestmentStatus =
  | 'draft'
  | 'pending_signature'
  /** RFC 0029 — firmado, esperando que PagaCel confirme el fondeo. */
  | 'pending_funding'
  | 'active'
  | 'matured'
  | 'early_terminated'
  | 'suspended'
  | 'cancelled'
  | 'pending'
  | 'completed';

/**
 * Investment list item (mapped from API response)
 */
export interface InvestmentListItem {
  id: string;
  contractNumber: string;
  /** User-settable display name; null by default. Fall back to contractNumber in UI. */
  displayName: string | null;
  productName: string | null;
  productCode: string | null;
  status: InvestmentStatus;
  /**
   * RFC 0029 — derivado de `funded_at` en el BE: indica si el principal ya
   * entró. Preferir esto sobre comparar `status`, que cambia según el flag
   * `investment_pending_funding_state`.
   */
  funded: boolean;
  currency: string;
  principalCents: number;
  /** Normalised to number (BE may return string or number) */
  interestRatePct: number;
  termMonths: number;
  startDate: Date;
  maturityDate: Date;
  reinvestAtMaturity: boolean;
  createdAt: Date;
}

/**
 * Pagination metadata
 */
export interface InvestmentsPaginationMeta {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

/**
 * Parameters for useInvestments hook
 */
export interface UseInvestmentsParams {
  /** Acepta un arreglo para pedir varios estados en una sola query. */
  status?: InvestmentStatus | InvestmentStatus[];
  sort?: string;
  maturingBefore?: string;
  page?: number;
  perPage?: number;
}

/**
 * Hook para listar inversiones del usuario
 * GET /api/v1/investments
 */
export function useInvestments(params?: UseInvestmentsParams) {
  return useQuery({
    queryKey: ['investments', params],
    queryFn: async (): Promise<{
      data: InvestmentListItem[];
      meta: InvestmentsPaginationMeta;
    }> => {
      const response = await clientApi.investments({
        status: params?.status,
        sort: params?.sort,
        maturing_before: params?.maturingBefore,
        page: params?.page,
        per_page: params?.perPage,
      });

      return {
        data: response.data.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (inv: any): InvestmentListItem => ({
            id: inv.id,
            contractNumber: inv.contract_number,
            displayName: inv.display_name ?? null,
            productName: inv.product_name ?? null,
            productCode: inv.product_code ?? null,
            status: inv.status as InvestmentStatus,
            // RFC 0029: `funded` es el dato real sobre si el principal entró.
            // Contratos previos al cambio no lo traen — se asume no fondeado
            // sólo para efectos de etiqueta, nunca para ocultar la fila.
            funded: inv.funded ?? false,
            // BE may return currency as an object {code, name, ...} or a string
            currency:
              typeof inv.currency === 'object'
                ? (inv.currency?.code ?? 'MXN')
                : (inv.currency ?? 'MXN'),
            principalCents: inv.principal_cents,
            interestRatePct: Number(inv.interest_rate_pct),
            termMonths: inv.term_months,
            startDate: new Date(inv.start_date),
            maturityDate: new Date(inv.maturity_date),
            reinvestAtMaturity: inv.reinvest_at_maturity ?? false,
            createdAt: new Date(inv.created_at),
          })
        ),
        meta: {
          currentPage: response.meta.current_page,
          totalPages: response.meta.total_pages,
          totalCount: response.meta.total_count,
          perPage: response.meta.per_page,
        },
      };
    },
    staleTime: 30000,
  });
}

/**
 * Portfolio summary (mapped from API)
 */
export interface InvestmentsPortfolioSummary {
  totalContracts: number;
  activeContracts: number;
  totalPrincipalCents: number;
  totalAccruedInterestCents: number;
  totalPaidInterestCents: number;
  totalWithheldTaxCents: number;
  aumCents: number;
  currency: string;
  averageRateBps: number;
  averageTermMonths: number;
}

export interface UpcomingMaturity {
  window: string;
  count: number;
  principalCents: number;
}

export interface InvestmentsPortfolio {
  asOfDate: Date;
  summary: InvestmentsPortfolioSummary;
  upcomingMaturities: UpcomingMaturity[];
}

/**
 * Hook para obtener métricas del portafolio de inversiones
 * GET /api/v1/investments/portfolio
 */
export function useInvestmentsPortfolio(asOfDate?: string) {
  return useQuery({
    queryKey: ['investments', 'portfolio', asOfDate],
    queryFn: async (): Promise<InvestmentsPortfolio> => {
      const response = await clientApi.investmentsPortfolio({
        as_of_date: asOfDate,
      });

      return {
        asOfDate: new Date(response.data.as_of_date),
        summary: {
          totalContracts: response.data.summary.total_contracts,
          activeContracts: response.data.summary.active_contracts,
          totalPrincipalCents: response.data.summary.total_principal_cents,
          totalAccruedInterestCents: response.data.summary.total_accrued_interest_cents,
          totalPaidInterestCents: response.data.summary.total_paid_interest_cents,
          totalWithheldTaxCents: response.data.summary.total_withheld_tax_cents,
          aumCents: response.data.summary.aum_cents,
          currency: response.data.summary.currency || 'MXN',
          averageRateBps: response.data.summary.average_rate_bps,
          averageTermMonths: response.data.summary.average_term_months,
        },
        upcomingMaturities: response.data.upcoming_maturities.map(m => ({
          window: m.window,
          count: m.count,
          principalCents: m.principal_cents,
        })),
      };
    },
    staleTime: 30000,
  });
}

/**
 * Hook para simular inversión con el backend
 * POST /api/v1/investments/simulate
 */
export function useSimulateInvestment() {
  return useMutation({
    mutationFn: (data: {
      amount_cents: number;
      term_months: number;
      // product_code: el BE deriva la tasa del cuadro; rate_bps queda como
      // fallback opcional (compat con callers viejos).
      product_code?: string;
      rate_bps?: number;
      start_date: string;
    }) => clientApi.simulateInvestment(data),
  });
}

/**
 * Data for creating an investment
 */
export interface CreateInvestmentData {
  product_code?: string;
  product_version_id?: string;
  amount_cents: number;
  currency: string;
  term_months: number;
  interest_payment_frequency: 'monthly' | 'quarterly' | 'at_maturity';
  reinvest_at_maturity: boolean;
  auto_renewal_term_months?: number;
  start_date: string;
  beneficiaries?: Array<{
    party_id?: string;
    name: string;
    percentage: number;
    relationship: 'spouse' | 'child' | 'parent' | 'sibling' | 'other';
  }>;
  metadata?: Record<string, unknown>;
}

/**
 * Hook para crear inversión con soporte de Step-Up MFA
 * POST /api/v1/investments
 *
 * Para inversiones >= 500,000 centavos, el backend retorna SEC-005
 * requiriendo verificación MFA adicional.
 */
export function useCreateInvestment() {
  // Preserve idempotency key across retries with step-up token
  const idempotencyKeyRef = useRef<string | null>(null);

  return useMutation({
    mutationFn: async ({
      data,
      stepUpToken,
    }: {
      data: CreateInvestmentData;
      stepUpToken?: string;
    }) => {
      // Generate idempotency key only on first attempt
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = crypto.randomUUID();
      }

      const result = await clientApi.createInvestment(data, idempotencyKeyRef.current, stepUpToken);

      // Clear key after success
      idempotencyKeyRef.current = null;

      return result;
    },
    onError: () => {
      // Keep the key for retries with step-up token
    },
  });
}

// ============================================================================
// CREDITS HOOKS
// ============================================================================

export interface CreditResponse {
  id: string;
  folio: string;
  createdAt: Date;
  valueCents: number;
  approval: 'approved' | 'pending' | 'rejected';
  status: 'active' | 'pending' | 'completed' | 'cancelled';
}

export interface CreditsSummary {
  totalCreditLimitCents: number;
  usedCreditCents: number;
  activeCount: number;
}

export function useCredits() {
  return useQuery({
    queryKey: ['credits'],
    queryFn: async (): Promise<CreditResponse[]> => {
      const response = await clientApi.credits();

      return response.data.map(
        (crd): CreditResponse => ({
          id: crd.id,
          folio: crd.folio,
          createdAt: new Date(crd.created_at),
          valueCents: crd.value_cents,
          approval: crd.approval,
          status: crd.status,
        })
      );
    },
    staleTime: 30000,
  });
}

export function useCreditsSummary() {
  return useQuery({
    queryKey: ['credits', 'summary'],
    queryFn: async (): Promise<CreditsSummary> => {
      const data = await clientApi.creditsPortfolio();

      return {
        totalCreditLimitCents: data.portfolio_summary.total_disbursed_cents,
        usedCreditCents: data.portfolio_summary.total_portfolio_cents,
        activeCount: data.portfolio_summary.active_contracts,
      };
    },
    staleTime: 30000,
  });
}

// ============================================================================
// SAVINGS BOX HOOKS
// ============================================================================

export interface SavingsBoxResponse {
  id: string;
  folio: string;
  name: string;
  endDate: Date;
  amountCents: number;
  status: 'active' | 'pending' | 'completed' | 'cancelled';
}

export interface SavingsBoxSummary {
  totalAmountCents: number;
  activeCount: number;
}

export interface SavingsPortfolio {
  summary: {
    total_accounts: number;
    active_accounts: number;
    total_balance_cents: number;
    currency: string;
  };
  contributions: {
    this_month_count: number;
    compliance_rate: number;
  };
  goals: {
    accounts_with_goals: number;
    goals_reached: number;
    goals_in_progress: number;
    average_progress_percentage: number;
  };
  retention: {
    opened_last_30_days: number;
    closed_last_30_days: number;
    net_growth: number;
  };
}

export function useSavingsBoxes() {
  return useQuery({
    queryKey: ['savings-boxes'],
    queryFn: async (): Promise<SavingsBoxResponse[]> => {
      const response = await clientApi.savingsBoxes();

      return response.data.map(
        (sb: Record<string, unknown>): SavingsBoxResponse => ({
          id: sb.id as string,
          folio: sb.folio as string,
          name: sb.name as string,
          endDate: new Date(sb.end_date as string),
          amountCents: sb.amount_cents as number,
          status: sb.status as 'pending' | 'completed' | 'cancelled' | 'active',
        })
      );
    },
    staleTime: 30000,
  });
}

export function useSavingsBoxSummary() {
  return useQuery({
    queryKey: ['savings-boxes', 'summary'],
    queryFn: async (): Promise<SavingsBoxSummary> => {
      const data = await clientApi.savingsBoxSummary();

      return {
        totalAmountCents: data.total_amount_cents,
        activeCount: data.active_count,
      };
    },
    staleTime: 30000,
  });
}

export function useSavingsPortfolio(as_of_date?: string) {
  return useQuery({
    queryKey: ['savings-portfolio', as_of_date],
    queryFn: async (): Promise<SavingsPortfolio> => {
      const response = await clientApi.savingsPortfolio(as_of_date);
      const d = response.data;
      return {
        summary: {
          total_accounts: d.summary.total_accounts,
          active_accounts: d.summary.active_accounts,
          total_balance_cents: d.summary.total_balance_cents,
          currency: d.summary.currency,
        },
        contributions: {
          this_month_count: d.contribution_metrics.contributions_this_month,
          compliance_rate: d.contribution_metrics.contribution_compliance_rate,
        },
        goals: {
          accounts_with_goals: d.goal_metrics.accounts_with_goals,
          goals_reached: d.goal_metrics.goals_reached,
          goals_in_progress: d.goal_metrics.goals_in_progress,
          average_progress_percentage: d.goal_metrics.average_progress_pct,
        },
        retention: {
          opened_last_30_days: d.retention_metrics.opened_last_30_days,
          closed_last_30_days: d.retention_metrics.closed_last_30_days,
          net_growth: d.retention_metrics.net_growth,
        },
      };
    },
    staleTime: 30000,
  });
}

// ============================================================================
// SAVINGS HOOKS
// ============================================================================

export interface SavingsBeneficiary {
  id: string;
  name: string;
  percentage: number;
  relationship: string;
}

export interface SavingsItem {
  id: string;
  contractNumber: string;
  productVersionId: string;
  productName: string;
  status: string;
  currentBalanceCents: number;
  currentBalance: { amount: string; currency: string };
  contributionAmountCents: number;
  contributionFrequency: string;
  nextContributionDate: string;
  goalName: string;
  goalAmountCents: number;
  goalAmount: { amount: string; currency: string };
  goalProgressPercentage: number;
  goalAchieved: boolean;
  totalContributedCents: number;
  totalContributed: { amount: string; currency: string };
  totalInterestEarnedCents: number;
  totalInterestEarned: { amount: string; currency: string };
  totalWithdrawnCents: number;
  totalWithdrawn: { amount: string; currency: string };
  contributionsCount: number;
  missedContributionsCount: number;
  startDate: string;
  maturityDate: string;
  beneficiaries: SavingsBeneficiary[];
  createdAt: Date;
  updatedAt: Date;
}

export interface SavingsMeta {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

export interface CreateSavingBeneficiary {
  partyId: string;
  percentage: number;
  relationship: string;
}

export interface CreateSavingInput {
  productCode: string;
  contributionAmountCents: number;
  contributionFrequency: string;
  initialDepositCents: number;
  initialPaymentReference: string;
  goalName: string;
  goalAmountCents: number;
  autoDebitEnabled: boolean;
  beneficiaries: CreateSavingBeneficiary[];
  metadata: Record<string, unknown>;
}

// TODO: Implement properly - createSavings requires idempotencyKey and stepUpToken
// This hook is not currently used. Implementation pending API integration review.
// export function useCreateSaving() {
//   const queryClient = useQueryClient();
//
//   return useMutation({
//     mutationFn: async (data: CreateSavingInput): Promise<SavingsItem> => {
//       // Implementation pending
//     },
//     onSuccess: () => {
//       queryClient.invalidateQueries({ queryKey: ['savings'] });
//       queryClient.invalidateQueries({ queryKey: ['savings-portfolio'] });
//     },
//   });
// }

// ============================================================================
// CONTACTS HOOKS
// ============================================================================

export interface ContactResponse {
  id: string;
  type: 'external' | 'tankef';
  firstName: string;
  lastName: string;
  clabe?: string;
  bankCode?: string;
  bankName?: string;
  alias?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string;
  createdAt: Date;
}

export interface ContactsSummary {
  totalCount: number;
  externalCount: number;
  tankefCount: number;
}

export function useContacts(params?: { type?: 'external' | 'tankef'; search?: string }) {
  return useQuery({
    queryKey: ['contacts', params],
    queryFn: async (): Promise<ContactResponse[]> => {
      const response = await clientApi.contacts(params);

      return response.data.map(
        (contact): ContactResponse => ({
          id: contact.id,
          type: contact.type,
          firstName: contact.first_name,
          lastName: contact.last_name,
          clabe: contact.clabe,
          bankCode: contact.bank_code,
          bankName: contact.bank_name,
          alias: contact.alias,
          email: contact.email,
          phone: contact.phone,
          avatarUrl: contact.avatar_url,
          createdAt: new Date(contact.created_at),
        })
      );
    },
    staleTime: 30000,
  });
}

export function useContactsSummary() {
  return useQuery({
    queryKey: ['contacts', 'summary'],
    queryFn: async (): Promise<ContactsSummary> => {
      const data = await clientApi.contactsSummary();

      return {
        totalCount: data.total_count,
        externalCount: data.external_count,
        tankefCount: data.tankef_count,
      };
    },
    staleTime: 30000,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof clientApi.createContact>[0]) =>
      clientApi.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof clientApi.updateContact>[1];
    }) => clientApi.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => clientApi.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

// ============================================================================
// RECIPIENTS HOOKS (Unified Contacts + Transfer Recipients)
// ============================================================================

export interface Recipient {
  id: string;
  source: 'contact' | 'transfer_recipient';
  sourceId: string;
  recipientType: 'internal' | 'external';
  displayName: string;
  alias?: string;
  favorite: boolean;
  contactPartyId?: string;
  accountId?: string;
  phoneMasked?: string;
  clabeMasked?: string;
  bankCode?: string;
  bankName?: string;
  transferMethod: 'wallet' | 'spei';
  transferCost: 'free' | 'fee';
  transferSpeed: 'instant' | 'minutes';
  status: 'active' | 'inactive' | 'pending';
  verified: boolean;
  resolutionMethod?: 'phone' | 'email' | 'clabe';
  createdAt: Date;
}

// Helper mapper (snake_case → camelCase)
function mapRecipientFromApi(data: {
  id: string;
  source: 'contact' | 'transfer_recipient';
  source_id: string;
  recipient_type: 'internal' | 'external';
  display_name: string;
  alias?: string;
  favorite: boolean;
  contact_party_id?: string;
  account_id?: string;
  phone_masked?: string;
  clabe_masked?: string;
  bank_code?: string;
  bank_name?: string;
  transfer_method: 'wallet' | 'spei';
  transfer_cost: 'free' | 'fee';
  transfer_speed: 'instant' | 'minutes';
  status: 'active' | 'inactive' | 'pending';
  verified: boolean;
  resolution_method?: 'phone' | 'email' | 'clabe';
  created_at: string;
}): Recipient {
  return {
    id: data.id,
    source: data.source,
    sourceId: data.source_id,
    recipientType: data.recipient_type,
    displayName: data.display_name,
    alias: data.alias,
    favorite: data.favorite,
    contactPartyId: data.contact_party_id,
    accountId: data.account_id,
    phoneMasked: data.phone_masked,
    clabeMasked: data.clabe_masked,
    bankCode: data.bank_code,
    bankName: data.bank_name,
    transferMethod: data.transfer_method,
    transferCost: data.transfer_cost,
    transferSpeed: data.transfer_speed,
    status: data.status,
    verified: data.verified,
    resolutionMethod: data.resolution_method,
    createdAt: new Date(data.created_at),
  };
}

/**
 * Hook para obtener lista de destinatarios
 * GET /api/v1/recipients
 */
export function useRecipients(params?: {
  favorite?: boolean;
  type?: 'internal' | 'external';
  search?: string;
}) {
  return useQuery({
    queryKey: ['recipients', params],
    queryFn: async (): Promise<Recipient[]> => {
      const response = await clientApi.recipients(params);
      return response.data.map(mapRecipientFromApi);
    },
    staleTime: 30000,
  });
}

/**
 * Hook para obtener un destinatario por ID
 * GET /api/v1/recipients/{id}
 */
export function useRecipient(id: string) {
  return useQuery({
    queryKey: ['recipients', id],
    queryFn: async (): Promise<Recipient> => {
      const response = await clientApi.recipient(id);
      return mapRecipientFromApi(response.data);
    },
    enabled: !!id,
  });
}

/**
 * Hook para crear destinatario
 * POST /api/v1/recipients
 */
export function useCreateRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof clientApi.createRecipient>[0]) =>
      clientApi.createRecipient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] });
    },
  });
}

/**
 * Hook para resolver/previsualizar destinatario
 * POST /api/v1/recipients/resolve
 */
export function useResolveRecipient() {
  return useMutation({
    mutationFn: (data: { identifier: string; identifier_type: 'clabe' | 'phone' | 'email' }) =>
      clientApi.resolveRecipient(data),
  });
}

/**
 * Hook para actualizar destinatario
 * PATCH /api/v1/recipients/{id}
 */
export function useUpdateRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof clientApi.updateRecipient>[1];
    }) => clientApi.updateRecipient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] });
    },
  });
}

/**
 * Hook para eliminar destinatario
 * DELETE /api/v1/recipients/{id}
 */
export function useDeleteRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientApi.deleteRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipients'] });
    },
  });
}

// ============================================================================
// MFA HOOKS (Multi-Factor Authentication - TOTP)
// ============================================================================

export interface MFAStatus {
  enabled: boolean;
  method: 'totp' | 'sms' | 'email' | null;
  backupCodesRemaining: number | null;
  lastVerifiedAt: Date | null;
  configuredAt: Date | null;
}

export interface MFASetupData {
  secret: string;
  provisioningUri: string;
  qrCodeSvg: string;
  backupCodes: string[];
}

/**
 * Hook para obtener estado de MFA
 * GET /api/v1/mfa/status
 */
export function useMFAStatus() {
  return useQuery({
    queryKey: ['mfa', 'status'],
    queryFn: async (): Promise<MFAStatus> => {
      const response = await clientApi.mfaStatus();
      return {
        enabled: response.data.enabled,
        method: response.data.channel,
        backupCodesRemaining: response.data.remaining_backup_codes,
        lastVerifiedAt: response.data.last_used_at ? new Date(response.data.last_used_at) : null,
        configuredAt: response.data.enabled_at ? new Date(response.data.enabled_at) : null,
      };
    },
    staleTime: 30000,
  });
}

/**
 * Hook para iniciar configuración de MFA
 * POST /api/v1/mfa/setup
 */
export function useMFASetup() {
  return useMutation({
    mutationFn: async (): Promise<MFASetupData> => {
      const response = await clientApi.mfaSetup();
      return {
        secret: response.data.secret,
        provisioningUri: response.data.provisioning_uri,
        qrCodeSvg: response.data.qr_code_svg,
        backupCodes: response.data.backup_codes,
      };
    },
  });
}

/**
 * Hook para habilitar MFA después de verificar OTP
 * POST /api/v1/mfa/enable
 */
export function useMFAEnable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (otp: string) => clientApi.mfaEnable(otp),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });
    },
  });
}

/**
 * Hook para deshabilitar MFA
 * DELETE /api/v1/mfa
 */
export function useMFADisable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stepUpToken: string) => clientApi.mfaDisable(stepUpToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });
    },
  });
}

/**
 * Hook para verificar código OTP y obtener step-up token
 * POST /api/v1/mfa/verify
 */
/**
 * Hook para verificar código OTP y obtener step-up token
 * POST /api/v1/mfa/verify
 * @param purpose - 'login' (default) o 'operation_confirmation' para step-up MFA
 */
export function useMFAVerify() {
  return useMutation({
    mutationFn: ({ otp, purpose }: { otp: string; purpose?: 'login' | 'operation_confirmation' }) =>
      clientApi.mfaVerify(otp, purpose),
  });
}

/**
 * Hook para regenerar códigos de respaldo
 * POST /api/v1/mfa/backup-codes/regenerate
 */
export function useMFARegenerateBackupCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stepUpToken: string) => clientApi.mfaRegenerateBackupCodes(stepUpToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });
    },
  });
}

// ============================================================================
// PASSWORD RESET HOOKS (3-step OTP flow)
// ============================================================================

/**
 * Step 1 — solicita OTP. Siempre devuelve 200 (anti-enumeration).
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (data: { email: string; channel?: 'sms' | 'email' }) => {
      const response = await clientApi.requestPasswordReset(data);
      return response;
    },
  });
}

/**
 * Step 2 — verifica OTP y devuelve reset_session_token (TTL 15 min).
 */
export function useVerifyPasswordReset() {
  return useMutation({
    mutationFn: async (data: { email: string; code: string }) => {
      const response = await clientApi.verifyPasswordReset(data);
      return response;
    },
  });
}

/**
 * RFC 0022 — canjea el enlace de activación de una cuenta migrada por un
 * reset_session_token. No recibe email: el enlace no lo lleva y el token trae
 * el tenant embebido. El siguiente paso es `useConfirmPasswordReset`.
 */
export function useVerifyAccountClaim() {
  return useMutation({
    mutationFn: async (data: { token: string }) => {
      const response = await clientApi.verifyAccountClaim(data);
      return response;
    },
  });
}

/**
 * Step 3 — confirma nueva contraseña usando reset_session_token.
 */
export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (data: {
      reset_session_token: string;
      password: string;
      password_confirmation: string;
    }) => {
      const response = await clientApi.confirmPasswordReset(data);
      return response;
    },
  });
}

// ============================================================================
// KYC HOOKS
// ============================================================================

/**
 * Estado del KYC del USUARIO autenticado (`GET /api/v1/kyc/status`, apiClient
 * con access_token). No confundir con el KYC del onboarding, que va por
 * `…/sessions/{id}/kyc/status` con el token de sesión.
 *
 * `enabled` mira que HAYA access_token, y no es una optimización: sin él, la
 * petición responde 401, el interceptor global no encuentra refresh token,
 * borra credenciales y hace `window.location.href = '/login'`. En el alta de
 * Persona Moral eso EXPULSA al usuario a media captura y se lleva por delante
 * el borrador en memoria — lo veía `LegalRepConsent`, que consulta este estado
 * best-effort para ofrecer reusar un KYC ya verificado.
 *
 * Sin token la query queda idle: `data` es `undefined`, que es justo lo que el
 * consumidor best-effort interpreta como "no se puede reusar".
 */
export function useKycStatus() {
  return useQuery({
    queryKey: ['kyc', 'status'],
    queryFn: () => clientApi.kycStatus(),
    enabled: Boolean(getStoredToken()),
    staleTime: 30000,
  });
}

export function useStartKyc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clientApi.startKyc(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
  });
}

export function useUploadKycDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ kycId, data }: { kycId: string; data: FormData }) =>
      clientApi.uploadKycDocument(kycId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
  });
}

export function useVerifyKyc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kycId: string) => clientApi.verifyKyc(kycId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
  });
}

export function useRetryKyc() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kycId: string) => clientApi.retryKyc(kycId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
  });
}

// ============================================================================
// RE-EXPORTS FROM ./client/ DIRECTORY
// ============================================================================
export * from './client/useAuth';
export * from './client/useJaakVerification';
export * from './client/useOnboarding';
export {
  useInitializeNotifications,
  useNotificationsChannel,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from './client/useNotifications';
export { useTransactionLimitsValidation } from './client/useTransactionLimitsValidation';
export {
  useRecipientResolution,
  type IdentifierType,
  type ResolvedRecipient,
} from './client/useRecipientResolution';
export {
  useTransferFlow,
  type TransferFlowParams,
  type TransferResult,
} from './client/useTransferFlow';
